import { useCallback, useEffect, useState } from "react";
import {
  initialScene,
  hasSeenIntro,
  markCampaignIntroSeen,
  markStage1Cleared,
  markStage2Cleared,
  markStage3Cleared,
  markStage4Cleared,
  markStage5Cleared,
  markStage6Cleared,
  markStage7Cleared,
  markStage8Cleared,
  markStage9Cleared,
  markStage10Cleared,
  markStage11Cleared,
  markStage12Cleared,
  markStage13Cleared,
  markStage14Cleared,
  markStage15Cleared,
  getStage1State,
  getStage2State,
  getStage3State,
  getStage4State,
  getStage5State,
  getStage6State,
  getStage7State,
  getStage8State,
  getStage9State,
  getStage10State,
  getStage11State,
  getStage12State,
  getStage13State,
  getStage14State,
  getStage15State,
  resetCampaign,
  restartCurrentAct,
  type Scene,
} from "./campaign/campaignState.ts";
import {
  RIN_INTRO,
  RIN_OUTRO,
  BRAM_MEET,
  BRAM_OUTRO,
  SERA_MEET,
  SERA_OUTRO,
  ACT1_TO_ACT2_LINES,
  ACT1_NAME,
  ACT1_PINS,
  ACT1_MAP_BG,
  RIN_ID,
  BRAM_ID,
  SERA_ID,
  RIN_PORTRAIT,
  BRAM_PORTRAIT,
  SERA_PORTRAIT,
  ACT1_BG,
} from "./campaign/act1.ts";
import {
  ACT2_NAME,
  ACT2_PINS,
  ACT2_MAP_BG,
  ACT2_BG,
  ACT2_TO_ACT3_LINES,
  ACT3_MAP_BG,
  TOR_ID,
  NIA_ID,
  CALDER_ID,
  TOR_PORTRAIT,
  NIA_PORTRAIT,
  CALDER_PORTRAIT,
  TOR_MEET,
  TOR_OUTRO,
  NIA_MEET,
  NIA_OUTRO,
  CALDER_MEET,
  CALDER_OUTRO,
} from "./campaign/act2.ts";
import {
  ACT3_NAME,
  ACT3_PINS,
  ACT3_BG,
  ORNA_ID,
  MAREK_ID,
  IBRA_ID,
  LOWEN_ID,
  ORNA_PORTRAIT,
  ORNA_MEET,
  ORNA_OUTRO,
  MAREK_PORTRAIT,
  MAREK_MEET,
  MAREK_OUTRO,
  IBRA_PORTRAIT,
  IBRA_MEET,
  IBRA_OUTRO,
  LOWEN_PORTRAIT,
  LOWEN_MEET,
  LOWEN_OUTRO,
} from "./campaign/act3.ts";
import {
  ACT4_NAME,
  ACT4_PINS,
  ACT4_MAP_BG,
  ACT4_BG,
  ACT3_TO_ACT4_LINES,
  DRABEN_ID,
  SILEN_ID,
  KAIROS_ID,
  DRABEN_PORTRAIT,
  DRABEN_MEET,
  DRABEN_OUTRO,
  SILEN_PORTRAIT,
  SILEN_MEET,
  SILEN_OUTRO,
  KAIROS_PORTRAIT,
  KAIROS_MEET,
  KAIROS_OUTRO,
} from "./campaign/act4.ts";
import {
  ACT5_NAME,
  ACT5_PINS,
  ACT5_MAP_BG,
  ACT5_BG,
  ACT4_TO_ACT5_LINES,
  ARDEN_ID,
  ARDEN_PORTRAIT,
  ARDEN_MEET,
  ARDEN_OUTRO,
  BELA_ID,
  BELA_PORTRAIT,
  BELA_MEET,
  BELA_OUTRO,
} from "./campaign/act5.ts";
import { TitleIntro, TITLE_LOGO } from "./campaign/TitleIntro.tsx";
import { Subtitles, PROLOGUE_BG } from "./campaign/Subtitles.tsx";
import { Ending } from "./campaign/Ending.tsx";
import { ENDING_CUTS } from "./campaign/ending.ts";
import { ActMap, type PinState } from "./campaign/ActMap.tsx";
import { DialogueScene } from "./campaign/DialogueScene.tsx";
import { MatchScreen } from "./MatchScreen.tsx";
import { Interstitial } from "../gamzone/ads/Interstitial.tsx";
import { ADMOB_INTERSTITIAL_UNIT_ID } from "../gamzone/ads/config.ts";
import { CTA_EVENT, RESET_EVENT } from "../app/gameConfig.ts";
import { loc, type Loc } from "./campaign/loc.ts";
import { useLang } from "../gamzone/i18n.ts";
import { bgm } from "./audio/bgm.ts";
import "./game.css";
import "./campaign/campaign.css";

// Scene → BGM grouping. 막 I–III(ally) and 막 IV–V(villain) each share one
// narration track across their map/dialogue scenes — re-entering a scene in
// the same group is a no-op for bgm.play (see bgm.ts), so it just keeps
// playing (or stays silent once its one pass has finished) rather than
// restarting. Match scenes loop; everything else plays once.
const NARRATION_ALLY_SCENES = new Set<Scene>([
  "prologue",
  "rinIntro", "rinOutro", "bramMeet", "bramOutro", "seraMeet", "seraOutro", "act1Done",
  "torMeet", "torOutro", "niaMeet", "niaOutro", "calderMeet", "calderOutro", "act2Done",
  "ornaMeet", "ornaOutro", "marekMeet", "marekOutro", "ibraMeet", "ibraOutro",
  "rowenMeet", "rowenOutro", "act3Done",
]);
const NARRATION_VILLAIN_SCENES = new Set<Scene>([
  "drabenMeet", "drabenOutro", "silenMeet", "silenOutro", "kairosMeet", "kairosOutro", "act4Done",
  "ardenMeet", "ardenOutro", "belaMeet", "belaOutro",
]);
const MATCH_ALLY_SCENES = new Set<Scene>([
  "match", "bramMatch", "seraMatch", "torMatch", "niaMatch", "calderMatch",
  "ornaMatch", "marekMatch", "ibraMatch", "rowenMatch",
]);
const MATCH_VILLAIN_SCENES = new Set<Scene>([
  "drabenMatch", "silenMatch", "kairosMatch", "ardenMatch", "belaMatch",
]);

const INTRO_BG = "/assets/backgrounds/intro-moss-gate-painterly-v2.webp";

const L = (ko: string, en: string): Loc => ({ ko, en });

// Reused across most stage outros — the map-return hint.
const TO_MAP: Loc = L("지도로", "To the map");
const CONTINUE_TO_ENDING: Loc = L("계속", "Continue");
const TO_GORGE: Loc = L("협곡으로", "To the gorge");
const DEEP_INTO_GORGE: Loc = L("깊은 협곡으로", "Deeper into the gorge");
const TO_ORNA: Loc = L("해독사 오르나에게로", "To Orna the decoder");
const TO_DRAVEN: Loc = L("붉은 창 드라벤에게로", "To Draven, the red lance");
const TO_ARDEN: Loc = L("사슬에 묶인 아르덴에게로", "To Arden, bound in chains");

/**
 * Campaign flow controller. Renders one scene of
 *
 *   titleIntro → prologue → mapReveal → mapReady → rinIntro → match ─┐
 *        │           ↑         ↑    └── rinOutro ←─────────┘ win
 *        │           │         └── bramMeet → bramMatch → bramOutro (win)
 *        └──────────────────────────→ mapReady (returning player, skips prologue)
 *
 * inside the shell's 9:16 .canvas-stack. Every fresh mount shows titleIntro
 * (loading + cover, see campaignState's initialScene); its tap goes to the
 * prologue for a new save or straight to mapReady for a returning one
 * (hasSeenIntro), which restores pin states from saved progress.
 */
export function Game() {
  const lang = useLang();
  const [scene, setScene] = useState<Scene>(initialScene);
  const go = useCallback((s: Scene) => setScene(s), []);

  // Which 막's map mapReady/mapReveal shows. null = "follow the furthest 막
  // reached" (the normal, automatic behaviour); a number pins the view to
  // that 막 instead, so browsing back to an earlier 막 via the map's ‹ › arrows
  // (ActMap's onPrevAct/onNextAct) sticks — including through replaying an
  // old stage from it, since that stage's onStart always returns here via
  // go("mapReady") without touching this. Reset to null on 다시 시작/처음으로
  // so those still land on the real current 막.
  const [viewedActOverride, setViewedActOverride] = useState<number | null>(
    null,
  );

  // Interstitial ad gate between acts — set to the bridge scene name
  // ("act1Done" etc.) to show first; onContinue clears it and proceeds. See
  // goViaAd below and gamzone/ads/Interstitial.tsx.
  const [adGate, setAdGate] = useState<Scene | null>(null);
  const goViaAd = useCallback((next: Scene) => setAdGate(next), []);

  // Right-panel test buttons:
  //  · "다시 시작" (CTA_EVENT) → replay the current 막 from its opening narration
  //    (막 I → prologue, 막 II → 막 II intro subtitle), from anywhere incl. a match.
  //  · "처음으로" (RESET_EVENT) → wipe everything, back to the 막 I prologue.
  useEffect(() => {
    const onCta = () => {
      setViewedActOverride(null);
      setAdGate(null);
      setScene(restartCurrentAct());
    };
    const onReset = () => {
      setViewedActOverride(null);
      setAdGate(null);
      resetCampaign();
      setScene("titleIntro");
    };
    window.addEventListener(CTA_EVENT, onCta);
    window.addEventListener(RESET_EVENT, onReset);
    return () => {
      window.removeEventListener(CTA_EVENT, onCta);
      window.removeEventListener(RESET_EVENT, onReset);
    };
  }, []);

  // Hoisted out of the mapReveal/mapReady case below (which still uses these)
  // so the BGM effect can also read viewedAct — the map's ‹ › browse arrows
  // should swap the narration track too when they step across the 막 III/IV
  // ally/villain boundary.
  const s1Cleared = getStage1State() === "cleared";
  const s2Cleared = getStage2State() === "cleared";
  const s3Cleared = getStage3State() === "cleared";
  const s4Cleared = getStage4State() === "cleared";
  const s5Cleared = getStage5State() === "cleared";
  const s6Cleared = getStage6State() === "cleared";
  const s7Cleared = getStage7State() === "cleared";
  const s8Cleared = getStage8State() === "cleared";
  const s9Cleared = getStage9State() === "cleared";
  const s10Cleared = getStage10State() === "cleared";
  const s11Cleared = getStage11State() === "cleared";
  const s12Cleared = getStage12State() === "cleared";
  const s13Cleared = getStage13State() === "cleared";
  const s14Cleared = getStage14State() === "cleared";
  const s15Cleared = getStage15State() === "cleared";
  const furthestAct = s13Cleared ? 5 : s10Cleared ? 4 : s6Cleared ? 3 : s3Cleared ? 2 : 1;
  const viewedAct = Math.min(viewedActOverride ?? furthestAct, furthestAct);

  useEffect(() => {
    if (scene === "titleIntro") bgm.play("intro");
    else if (scene === "ending") bgm.play("ending");
    else if (scene === "mapReveal" || scene === "mapReady") {
      bgm.play(viewedAct <= 3 ? "narrationAlly" : "narrationVillain");
    } else if (NARRATION_ALLY_SCENES.has(scene)) bgm.play("narrationAlly");
    else if (NARRATION_VILLAIN_SCENES.has(scene)) bgm.play("narrationVillain");
    else if (MATCH_ALLY_SCENES.has(scene)) bgm.play("matchAlly");
    else if (MATCH_VILLAIN_SCENES.has(scene)) bgm.play("matchVillain");
  }, [scene, viewedAct]);

  if (adGate) {
    return (
      <Interstitial
        real
        adUnitId={ADMOB_INTERSTITIAL_UNIT_ID}
        onContinue={() => {
          const next = adGate;
          setAdGate(null);
          go(next);
        }}
      />
    );
  }

  switch (scene) {
    case "titleIntro":
      return (
        <TitleIntro
          bg={INTRO_BG}
          preloadUrls={[INTRO_BG, PROLOGUE_BG, TITLE_LOGO]}
          onStart={() => go(hasSeenIntro() ? "mapReady" : "prologue")}
        />
      );

    case "prologue":
      return (
        <Subtitles
          onDone={() => {
            markCampaignIntroSeen();
            go("mapReveal");
          }}
        />
      );

    case "mapReveal":
    case "mapReady": {
      // s1Cleared..s15Cleared, furthestAct and viewedAct are hoisted above
      // (the BGM effect needs viewedAct too — see the ‹ › browse-arrow note there).
      const onPrevAct =
        viewedAct > 1
          ? () => setViewedActOverride(viewedAct - 1)
          : undefined;
      const onNextAct =
        viewedAct < furthestAct
          ? () => setViewedActOverride(viewedAct + 1)
          : undefined;

      if (viewedAct === 5) {
        const stateOf5 = (id: number): PinState => {
          if (id === ARDEN_ID) return s14Cleared ? "cleared" : "active";
          if (id === BELA_ID) {
            if (!s14Cleared) return "locked";
            return s15Cleared ? "cleared" : "active";
          }
          return "locked";
        };

        const prompt5 = loc(
          !s14Cleared
            ? L("벨라에게 빼앗긴 아르덴의 자아를 되돌리세요", "Restore Arden's self, stolen by Vela")
            : !s15Cleared
              ? L("왕좌의 벨라를 저지하세요", "Stop Vela on the throne")
              : L("무너졌던 봉인을 완전히 되찾았습니다", "The broken seal has been fully restored"),
          lang,
        );

        return (
          <ActMap
            phase={scene}
            actName={loc(ACT5_NAME, lang)}
            pins={ACT5_PINS}
            mapBg={ACT5_MAP_BG}
            stateOf={stateOf5}
            prompt={prompt5}
            onRevealDone={() => go("mapReady")}
            onPick={(id) => {
              if (id === ARDEN_ID) go("ardenMeet");
              else if (id === BELA_ID) go("belaMeet");
            }}
            onPrevAct={onPrevAct}
            onNextAct={onNextAct}
          />
        );
      }

      if (viewedAct === 4) {
        const stateOf4 = (id: number): PinState => {
          if (id === DRABEN_ID) return s11Cleared ? "cleared" : "active";
          if (id === SILEN_ID) {
            if (!s11Cleared) return "locked";
            return s12Cleared ? "cleared" : "active";
          }
          if (id === KAIROS_ID) {
            if (!s12Cleared) return "locked";
            return s13Cleared ? "cleared" : "active";
          }
          return "locked";
        };

        const prompt4 = loc(
          !s11Cleared
            ? L("궁정 입구를 막은 드라벤을 뚫으세요", "Break through Draven at the court entrance")
            : !s12Cleared
              ? L("기록의 방에서 실렌의 수정책을 되찾으세요", "Reclaim Silen's crystal book in the records room")
              : !s13Cleared
                ? L("황혼의 사냥문에서 카이로스에게 투항한 린을 되찾으세요", "Bring Rin back from Kairos at the dusk hunt gate")
                : L("카이로스의 사냥문을 넘었습니다", "Passed Kairos's hunt gate"),
          lang,
        );

        return (
          <ActMap
            phase={scene}
            actName={loc(ACT4_NAME, lang)}
            pins={ACT4_PINS}
            mapBg={ACT4_MAP_BG}
            stateOf={stateOf4}
            prompt={prompt4}
            onRevealDone={() => go("mapReady")}
            onPick={(id) => {
              if (id === DRABEN_ID) go("drabenMeet");
              else if (id === SILEN_ID) go("silenMeet");
              else if (id === KAIROS_ID) go("kairosMeet");
            }}
            onPrevAct={onPrevAct}
            onNextAct={onNextAct}
          />
        );
      }

      if (viewedAct === 3) {
        const stateOf3 = (id: number): PinState => {
          if (id === ORNA_ID) return s7Cleared ? "cleared" : "active";
          if (id === MAREK_ID) {
            if (!s7Cleared) return "locked";
            return s8Cleared ? "cleared" : "active";
          }
          if (id === IBRA_ID) {
            if (!s8Cleared) return "locked";
            return s9Cleared ? "cleared" : "active";
          }
          if (id === LOWEN_ID) {
            if (!s9Cleared) return "locked";
            return s10Cleared ? "cleared" : "active";
          }
          return "locked";
        };

        const prompt3 = loc(
          !s7Cleared
            ? L("오르나와 마지막 비문을 읽어 내세요", "Read the last inscription with Orna")
            : !s8Cleared
              ? L("마레크의 관문에서 청동 인장을 얻으세요", "Win the bronze seal at Marek's gate")
              : !s9Cleared
                ? L("이브라와 위장 결투로 정찰병을 따돌리세요", "Throw off the scouts with a diversion duel alongside Ivra")
                : !s10Cleared
                  ? L("로웬의 공개 재판을 통과하세요", "Pass Rowen's open trial")
                  : L("로웬의 판결을 통과했습니다", "Passed Rowen's judgment"),
          lang,
        );

        return (
          <ActMap
            phase={scene}
            actName={loc(ACT3_NAME, lang)}
            pins={ACT3_PINS}
            mapBg={ACT3_MAP_BG}
            stateOf={stateOf3}
            prompt={prompt3}
            onRevealDone={() => go("mapReady")}
            onPick={(id) => {
              if (id === ORNA_ID) go("ornaMeet");
              else if (id === MAREK_ID) go("marekMeet");
              else if (id === IBRA_ID) go("ibraMeet");
              else if (id === LOWEN_ID) go("rowenMeet");
            }}
            onPrevAct={onPrevAct}
            onNextAct={onNextAct}
          />
        );
      }

      if (viewedAct === 2) {
        const stateOf2 = (id: number): PinState => {
          if (id === TOR_ID) return s4Cleared ? "cleared" : "active";
          if (id === NIA_ID) {
            if (!s4Cleared) return "locked";
            return s5Cleared ? "cleared" : "active";
          }
          if (id === CALDER_ID) {
            if (!s5Cleared) return "locked";
            return s6Cleared ? "cleared" : "active";
          }
          return "locked";
        };

        const prompt2 = loc(
          !s4Cleared
            ? L("토르와 함께 진짜 흔적을 찾으세요", "Find the true trail together with Tor")
            : !s5Cleared
              ? L("니아의 구슬에 번진 균열을 막으세요", "Push back the rift spreading through Nia's orb")
              : !s6Cleared
                ? L("칼데르의 관문 의식을 치르세요", "Complete Calder's gate rite")
                : L("칼데르를 이겼습니다", "Defeated Calder"),
          lang,
        );

        return (
          <ActMap
            phase={scene}
            actName={loc(ACT2_NAME, lang)}
            pins={ACT2_PINS}
            mapBg={ACT2_MAP_BG}
            stateOf={stateOf2}
            prompt={prompt2}
            onRevealDone={() => go("mapReady")}
            onPick={(id) => {
              if (id === TOR_ID) go("torMeet");
              else if (id === NIA_ID) go("niaMeet");
              else if (id === CALDER_ID) go("calderMeet");
            }}
            onPrevAct={onPrevAct}
            onNextAct={onNextAct}
          />
        );
      }

      const stateOf1 = (id: number): PinState => {
        if (id === RIN_ID) return s1Cleared ? "cleared" : "active";
        if (id === BRAM_ID) {
          if (!s1Cleared) return "locked";
          return s2Cleared ? "cleared" : "active";
        }
        if (id === SERA_ID) {
          if (!s2Cleared) return "locked";
          return s3Cleared ? "cleared" : "active";
        }
        return "locked";
      };

      const prompt1 = loc(
        !s1Cleared
          ? L("봉인석을 눌러 첫 결투를 시작하세요", "Tap the pact stone to start the first duel")
          : !s2Cleared
            ? L("길잡이 린과 함께 브람에게 가세요", "Head to Bram together with your guide Rin")
            : !s3Cleared
              ? L("세라와 함께 균열 짐승을 막으세요", "Hold off the rift beast alongside Sera")
              : L("세라와 짐승을 물리쳤습니다", "Beat the beast alongside Sera"),
        lang,
      );

      return (
        <ActMap
          phase={scene}
          actName={loc(ACT1_NAME, lang)}
          pins={ACT1_PINS}
          mapBg={ACT1_MAP_BG}
          stateOf={stateOf1}
          prompt={prompt1}
          onRevealDone={() => go("mapReady")}
          onPick={(id) => {
            if (id === RIN_ID) go("rinIntro");
            else if (id === BRAM_ID) go("bramMeet");
            else if (id === SERA_ID) go("seraMeet");
          }}
          onPrevAct={onPrevAct}
          onNextAct={onNextAct}
        />
      );
    }

    case "rinIntro":
      return (
        <DialogueScene
          lines={RIN_INTRO}
          bg={ACT1_BG}
          portraits={{ 린: RIN_PORTRAIT }}
          onStart={() => go("match")}
        />
      );

    case "match":
      return (
        <MatchScreen
          stageId={1}
          onExitToMap={() => go("mapReady")}
          onWin={() => {
            markStage1Cleared();
            go("rinOutro");
          }}
        />
      );

    case "rinOutro":
      return (
        <DialogueScene
          lines={RIN_OUTRO}
          bg={ACT1_BG}
          portraits={{ 린: RIN_PORTRAIT }}
          endLabel={TO_MAP}
          onStart={() => go("mapReady")}
        />
      );

    case "bramMeet":
      return (
        <DialogueScene
          lines={BRAM_MEET}
          bg={ACT1_BG}
          portraits={{ 브람: BRAM_PORTRAIT, 린: RIN_PORTRAIT }}
          onStart={() => go("bramMatch")}
        />
      );

    case "bramMatch":
      return (
        <MatchScreen
          stageId={2}
          onExitToMap={() => go("mapReady")}
          onWin={() => {
            markStage2Cleared();
            go("bramOutro");
          }}
        />
      );

    case "bramOutro":
      return (
        <DialogueScene
          lines={BRAM_OUTRO}
          bg={ACT1_BG}
          portraits={{ 브람: BRAM_PORTRAIT }}
          endLabel={TO_MAP}
          onStart={() => go("mapReady")}
        />
      );

    case "seraMeet":
      return (
        <DialogueScene
          lines={SERA_MEET}
          bg={ACT1_BG}
          portraits={{ 세라: SERA_PORTRAIT, 린: RIN_PORTRAIT }}
          onStart={() => go("seraMatch")}
        />
      );

    case "seraMatch":
      return (
        <MatchScreen
          stageId={3}
          onExitToMap={() => go("mapReady")}
          onWin={() => {
            markStage3Cleared();
            go("seraOutro");
          }}
        />
      );

    case "seraOutro":
      return (
        <DialogueScene
          lines={SERA_OUTRO}
          bg={ACT1_BG}
          portraits={{ 세라: SERA_PORTRAIT, 브람: BRAM_PORTRAIT, 린: RIN_PORTRAIT }}
          endLabel={TO_GORGE}
          onStart={() => goViaAd("act1Done")}
        />
      );

    case "act1Done":
      // Subtitle over the 협곡 map, then its pins reveal one by one — the same
      // "narration → pins appear" beat the prologue gives Act I.
      return (
        <Subtitles
          lines={ACT1_TO_ACT2_LINES}
          bg={ACT2_MAP_BG}
          lastHint={DEEP_INTO_GORGE}
          onDone={() => go("mapReveal")}
        />
      );

    case "torMeet":
      return (
        <DialogueScene
          lines={TOR_MEET}
          bg={ACT2_BG}
          portraits={{ 토르: TOR_PORTRAIT, 세라: SERA_PORTRAIT, 린: RIN_PORTRAIT }}
          onStart={() => go("torMatch")}
        />
      );

    case "torMatch":
      return (
        <MatchScreen
          stageId={4}
          onExitToMap={() => go("mapReady")}
          onWin={() => {
            markStage4Cleared();
            go("torOutro");
          }}
        />
      );

    case "torOutro":
      return (
        <DialogueScene
          lines={TOR_OUTRO}
          bg={ACT2_BG}
          portraits={{ 토르: TOR_PORTRAIT, 브람: BRAM_PORTRAIT }}
          endLabel={TO_MAP}
          onStart={() => go("mapReady")}
        />
      );

    case "niaMeet":
      return (
        <DialogueScene
          lines={NIA_MEET}
          bg={ACT2_BG}
          portraits={{ 니아: NIA_PORTRAIT, 브람: BRAM_PORTRAIT }}
          onStart={() => go("niaMatch")}
        />
      );

    case "niaMatch":
      return (
        <MatchScreen
          stageId={5}
          onExitToMap={() => go("mapReady")}
          onWin={() => {
            markStage5Cleared();
            go("niaOutro");
          }}
        />
      );

    case "niaOutro":
      return (
        <DialogueScene
          lines={NIA_OUTRO}
          bg={ACT2_BG}
          portraits={{ 니아: NIA_PORTRAIT, 토르: TOR_PORTRAIT }}
          endLabel={TO_MAP}
          onStart={() => go("mapReady")}
        />
      );

    case "calderMeet":
      return (
        <DialogueScene
          lines={CALDER_MEET}
          bg={ACT2_BG}
          portraits={{ 칼데르: CALDER_PORTRAIT, 토르: TOR_PORTRAIT, 세라: SERA_PORTRAIT }}
          onStart={() => go("calderMatch")}
        />
      );

    case "calderMatch":
      return (
        <MatchScreen
          stageId={6}
          onExitToMap={() => go("mapReady")}
          onWin={() => {
            markStage6Cleared();
            go("calderOutro");
          }}
        />
      );

    case "calderOutro":
      return (
        <DialogueScene
          lines={CALDER_OUTRO}
          bg={ACT2_BG}
          portraits={{
            칼데르: CALDER_PORTRAIT,
            토르: TOR_PORTRAIT,
            린: RIN_PORTRAIT,
            브람: BRAM_PORTRAIT,
          }}
          endLabel={TO_MAP}
          onStart={() => goViaAd("act2Done")}
        />
      );

    case "act2Done":
      // Bridge subtitle over the 달빛 폐허 map, same beat as act1Done — then the
      // Act III pins reveal one by one (s6Cleared branch in mapReveal/mapReady).
      return (
        <Subtitles
          lines={ACT2_TO_ACT3_LINES}
          bg={ACT3_MAP_BG}
          lastHint={TO_ORNA}
          onDone={() => go("mapReveal")}
        />
      );

    case "ornaMeet":
      return (
        <DialogueScene
          lines={ORNA_MEET}
          bg={ACT3_BG}
          portraits={{ 오르나: ORNA_PORTRAIT, 브람: BRAM_PORTRAIT }}
          onStart={() => go("ornaMatch")}
        />
      );

    case "ornaMatch":
      return (
        <MatchScreen
          stageId={7}
          onExitToMap={() => go("mapReady")}
          onWin={() => {
            markStage7Cleared();
            go("ornaOutro");
          }}
        />
      );

    case "ornaOutro":
      return (
        <DialogueScene
          lines={ORNA_OUTRO}
          bg={ACT3_BG}
          portraits={{ 오르나: ORNA_PORTRAIT, 세라: SERA_PORTRAIT }}
          endLabel={TO_MAP}
          onStart={() => go("mapReady")}
        />
      );

    case "marekMeet":
      return (
        <DialogueScene
          lines={MAREK_MEET}
          bg={ACT3_BG}
          portraits={{
            마레크: MAREK_PORTRAIT,
            브람: BRAM_PORTRAIT,
            세라: SERA_PORTRAIT,
            린: RIN_PORTRAIT,
          }}
          onStart={() => go("marekMatch")}
        />
      );

    case "marekMatch":
      return (
        <MatchScreen
          stageId={8}
          onExitToMap={() => go("mapReady")}
          onWin={() => {
            markStage8Cleared();
            go("marekOutro");
          }}
        />
      );

    case "marekOutro":
      return (
        <DialogueScene
          lines={MAREK_OUTRO}
          bg={ACT3_BG}
          portraits={{ 마레크: MAREK_PORTRAIT, 세라: SERA_PORTRAIT }}
          endLabel={TO_MAP}
          onStart={() => go("mapReady")}
        />
      );

    case "ibraMeet":
      return (
        <DialogueScene
          lines={IBRA_MEET}
          bg={ACT3_BG}
          portraits={{
            이브라: IBRA_PORTRAIT,
            토르: TOR_PORTRAIT,
            세라: SERA_PORTRAIT,
          }}
          onStart={() => go("ibraMatch")}
        />
      );

    case "ibraMatch":
      return (
        <MatchScreen
          stageId={9}
          onExitToMap={() => go("mapReady")}
          onWin={() => {
            markStage9Cleared();
            go("ibraOutro");
          }}
        />
      );

    case "ibraOutro":
      return (
        <DialogueScene
          lines={IBRA_OUTRO}
          bg={ACT3_BG}
          portraits={{
            이브라: IBRA_PORTRAIT,
            세라: SERA_PORTRAIT,
            린: RIN_PORTRAIT,
            브람: BRAM_PORTRAIT,
          }}
          endLabel={TO_MAP}
          onStart={() => go("mapReady")}
        />
      );

    case "rowenMeet":
      return (
        <DialogueScene
          lines={LOWEN_MEET}
          bg={ACT3_BG}
          portraits={{ 로웬: LOWEN_PORTRAIT, 린: RIN_PORTRAIT }}
          onStart={() => go("rowenMatch")}
        />
      );

    case "rowenMatch":
      return (
        <MatchScreen
          stageId={10}
          onExitToMap={() => go("mapReady")}
          onWin={() => {
            markStage10Cleared();
            go("rowenOutro");
          }}
        />
      );

    case "rowenOutro":
      return (
        <DialogueScene
          lines={LOWEN_OUTRO}
          bg={ACT3_BG}
          portraits={{
            로웬: LOWEN_PORTRAIT,
            토르: TOR_PORTRAIT,
            세라: SERA_PORTRAIT,
          }}
          endLabel={TO_MAP}
          onStart={() => goViaAd("act3Done")}
        />
      );

    case "act3Done":
      // Bridge subtitle over the 가시 궁정 map, same beat as act1Done/act2Done —
      // then the Act IV pins reveal (s10Cleared branch in mapReveal/mapReady).
      return (
        <Subtitles
          lines={ACT3_TO_ACT4_LINES}
          bg={ACT4_MAP_BG}
          lastHint={TO_DRAVEN}
          onDone={() => go("mapReveal")}
        />
      );

    case "drabenMeet":
      return (
        <DialogueScene
          lines={DRABEN_MEET}
          bg={ACT4_BG}
          portraits={{ 드라벤: DRABEN_PORTRAIT, 브람: BRAM_PORTRAIT }}
          onStart={() => go("drabenMatch")}
        />
      );

    case "drabenMatch":
      return (
        <MatchScreen
          stageId={11}
          onExitToMap={() => go("mapReady")}
          onWin={() => {
            markStage11Cleared();
            go("drabenOutro");
          }}
        />
      );

    case "drabenOutro":
      return (
        <DialogueScene
          lines={DRABEN_OUTRO}
          bg={ACT4_BG}
          portraits={{
            드라벤: DRABEN_PORTRAIT,
            토르: TOR_PORTRAIT,
            세라: SERA_PORTRAIT,
          }}
          endLabel={TO_MAP}
          onStart={() => go("mapReady")}
        />
      );

    case "silenMeet":
      return (
        <DialogueScene
          lines={SILEN_MEET}
          bg={ACT4_BG}
          portraits={{ 실렌: SILEN_PORTRAIT, 린: RIN_PORTRAIT }}
          onStart={() => go("silenMatch")}
        />
      );

    case "silenMatch":
      return (
        <MatchScreen
          stageId={12}
          onExitToMap={() => go("mapReady")}
          onWin={() => {
            markStage12Cleared();
            go("silenOutro");
          }}
        />
      );

    case "silenOutro":
      return (
        <DialogueScene
          lines={SILEN_OUTRO}
          bg={ACT4_BG}
          portraits={{
            실렌: SILEN_PORTRAIT,
            세라: SERA_PORTRAIT,
            린: RIN_PORTRAIT,
            토르: TOR_PORTRAIT,
          }}
          endLabel={TO_MAP}
          onStart={() => go("mapReady")}
        />
      );

    case "kairosMeet":
      return (
        <DialogueScene
          lines={KAIROS_MEET}
          bg={ACT4_BG}
          portraits={{
            카이로스: KAIROS_PORTRAIT,
            린: RIN_PORTRAIT,
            세라: SERA_PORTRAIT,
            브람: BRAM_PORTRAIT,
          }}
          onStart={() => go("kairosMatch")}
        />
      );

    case "kairosMatch":
      return (
        <MatchScreen
          stageId={13}
          onExitToMap={() => go("mapReady")}
          onWin={() => {
            markStage13Cleared();
            go("kairosOutro");
          }}
        />
      );

    case "kairosOutro":
      return (
        <DialogueScene
          lines={KAIROS_OUTRO}
          bg={ACT4_BG}
          portraits={{
            카이로스: KAIROS_PORTRAIT,
            린: RIN_PORTRAIT,
            토르: TOR_PORTRAIT,
            세라: SERA_PORTRAIT,
            브람: BRAM_PORTRAIT,
          }}
          endLabel={TO_MAP}
          onStart={() => goViaAd("act4Done")}
        />
      );

    case "act4Done":
      // Bridge subtitle over the 에메랄드 심장 map, same beat as act2Done/act3Done —
      // then the Act V pins reveal (s13Cleared branch in mapReveal/mapReady).
      return (
        <Subtitles
          lines={ACT4_TO_ACT5_LINES}
          bg={ACT5_MAP_BG}
          lastHint={TO_ARDEN}
          onDone={() => go("mapReveal")}
        />
      );

    case "ardenMeet":
      return (
        <DialogueScene
          lines={ARDEN_MEET}
          bg={ACT5_BG}
          portraits={{
            아르덴: ARDEN_PORTRAIT,
            토르: TOR_PORTRAIT,
            린: RIN_PORTRAIT,
            브람: BRAM_PORTRAIT,
          }}
          onStart={() => go("ardenMatch")}
        />
      );

    case "ardenMatch":
      return (
        <MatchScreen
          stageId={14}
          onExitToMap={() => go("mapReady")}
          onWin={() => {
            markStage14Cleared();
            go("ardenOutro");
          }}
        />
      );

    case "ardenOutro":
      return (
        <DialogueScene
          lines={ARDEN_OUTRO}
          bg={ACT5_BG}
          portraits={{
            아르덴: ARDEN_PORTRAIT,
            린: RIN_PORTRAIT,
            토르: TOR_PORTRAIT,
          }}
          endLabel={TO_MAP}
          onStart={() => go("mapReady")}
        />
      );

    case "belaMeet":
      return (
        <DialogueScene
          lines={BELA_MEET}
          bg={ACT5_BG}
          portraits={{
            벨라: BELA_PORTRAIT,
            린: RIN_PORTRAIT,
            토르: TOR_PORTRAIT,
          }}
          onStart={() => go("belaMatch")}
        />
      );

    case "belaMatch":
      return (
        <MatchScreen
          stageId={15}
          onExitToMap={() => go("mapReady")}
          onWin={() => {
            markStage15Cleared();
            go("belaOutro");
          }}
        />
      );

    case "belaOutro":
      return (
        <DialogueScene
          lines={BELA_OUTRO}
          bg={ACT5_BG}
          portraits={{
            벨라: BELA_PORTRAIT,
            토르: TOR_PORTRAIT,
            브람: BRAM_PORTRAIT,
            린: RIN_PORTRAIT,
            세라: SERA_PORTRAIT,
          }}
          endLabel={CONTINUE_TO_ENDING}
          onStart={() => go("ending")}
        />
      );

    case "ending":
      return <Ending cuts={ENDING_CUTS} />;
  }
}
